import React, { useState, useEffect } from 'react';
import { useReminders } from '../hooks/useReminders';
import { useNotifications } from '../hooks/useNotifications';
import { ReminderFormData, REMINDER_TEMPLATES } from '../types/reminders';
import './RemindersManager.css';

interface RemindersManagerProps {
  className?: string;
}

const RemindersManager: React.FC<RemindersManagerProps> = ({ className = '' }) => {
  const { reminders, isLoading, createReminder, updateReminder, deleteReminder, toggleReminder } = useReminders();
  const { permission, requestPermission } = useNotifications();
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ReminderFormData>({
    type: 'tension',
    title: 'Recordatorio de tensión',
    message: 'Es hora de medir tu tensión arterial',
    time: '21:00',
    frequency: 'daily',
    days_of_week: [],
    days_of_month: [],
    notification_method: 'push',
    is_active: true
  });

  const DAYS_OF_WEEK = [
    { value: 0, label: 'Dom', name: 'Domingo' },
    { value: 1, label: 'Lun', name: 'Lunes' },
    { value: 2, label: 'Mar', name: 'Martes' },
    { value: 3, label: 'Mié', name: 'Miércoles' },
    { value: 4, label: 'Jue', name: 'Jueves' },
    { value: 5, label: 'Vie', name: 'Viernes' },
    { value: 6, label: 'Sáb', name: 'Sábado' }
  ];

  const handleTemplateSelect = (templateId: string) => {
    const template = REMINDER_TEMPLATES.find(t => t.id === templateId);
    if (template) {
      setFormData(prev => ({
        ...prev,
        type: template.type,
        title: template.title,
        message: template.message
      }));
    }
  };

  const handleDayToggle = (day: number) => {
    setFormData(prev => ({
      ...prev,
      days_of_week: prev.days_of_week?.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...(prev.days_of_week || []), day]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        await updateReminder(editingId, formData);
      } else {
        await createReminder(formData);
      }
      
      // Reset form
      setFormData({
        type: 'tension',
        title: 'Recordatorio de tensión',
        message: 'Es hora de medir tu tensión arterial',
        time: '21:00',
        frequency: 'daily',
        days_of_week: [],
        days_of_month: [],
        notification_method: 'push',
        is_active: true
      });
      setShowForm(false);
      setEditingId(null);
    } catch (error) {
      console.error('Error al guardar recordatorio:', error);
      alert('Error al guardar el recordatorio');
    }
  };

  const handleEdit = (reminder: any) => {
    setFormData({
      type: reminder.type,
      title: reminder.title,
      message: reminder.message,
      time: reminder.time,
      frequency: reminder.frequency,
      days_of_week: reminder.days_of_week || [],
      days_of_month: reminder.days_of_month || [],
      notification_method: Array.isArray(reminder.notification_methods) 
        ? reminder.notification_methods[0] 
        : JSON.parse(reminder.notification_methods || '["push"]')[0],
      is_active: reminder.enabled
    });
    setEditingId(reminder.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('¿Estás seguro de eliminar este recordatorio?')) {
      await deleteReminder(id);
    }
  };

  const formatFrequency = (reminder: any) => {
    switch (reminder.frequency) {
      case 'daily':
        return 'Diario';
      case 'weekly':
        if (reminder.days_of_week && reminder.days_of_week.length > 0) {
          const dayNames = reminder.days_of_week
            .map((day: number) => DAYS_OF_WEEK.find(d => d.value === day)?.label)
            .join(', ');
          return `Semanal (${dayNames})`;
        }
        return 'Semanal';
      case 'monthly':
        return 'Mensual';
      default:
        return reminder.frequency;
    }
  };

  const formatNextSend = (nextSendAt: string) => {
    const date = new Date(nextSendAt);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
    
    if (diffHours <= 0) {
      return 'Próximamente';
    } else if (diffHours < 24) {
      return `En ${diffHours}h`;
    } else {
      const diffDays = Math.ceil(diffHours / 24);
      return `En ${diffDays}d`;
    }
  };

  // Bloquear scroll del body cuando el modal está abierto
  useEffect(() => {
    if (isExpanded) {
      // Guardar el scroll actual y bloquear scroll
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      // Restaurar scroll
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    }

    // Cleanup al desmontar
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isExpanded]);

  return (
    <div className={`reminders-manager ${className}`}>
      <button
        className="reminders-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        title="Gestionar recordatorios"
      >
        ⏰
        {reminders.filter(r => r.enabled).length > 0 && (
          <span className="reminders-count">
            {reminders.filter(r => r.enabled).length}
          </span>
        )}
      </button>

      {isExpanded && (
        <div className="reminders-panel">
          <div className="reminders-header">
            <h4>📅 Recordatorios</h4>
            <div className="header-actions">
              <button
                className="add-reminder-btn"
                onClick={() => {
                  setShowForm(true);
                  setEditingId(null);
                }}
              >
                ➕ Nuevo
              </button>
              <button 
                className="close-btn"
                onClick={() => setIsExpanded(false)}
              >
                ✕
              </button>
            </div>
          </div>

          <div className="reminders-content">
            {/* Verificar permisos de notificación */}
            {permission !== 'granted' && (
              <div className="permission-warning">
                <p>⚠️ Activa las notificaciones para recibir recordatorios</p>
                <button 
                  className="permission-btn"
                  onClick={requestPermission}
                >
                  Activar notificaciones
                </button>
              </div>
            )}

            {/* Lista de recordatorios existentes */}
            {reminders.length > 0 && (
              <div className="reminders-list">
                <h5>Tus recordatorios:</h5>
                {reminders.map(reminder => (
                  <div 
                    key={reminder.id} 
                    className={`reminder-item ${!reminder.enabled ? 'inactive' : ''}`}
                  >
                    <div className="reminder-info">
                      <div className="reminder-title">
                        <span className="reminder-icon">
                          {reminder.type === 'tension' ? '💓' : 
                           reminder.type === 'headache' ? '🤕' : '💊'}
                        </span>
                        {reminder.title}
                      </div>
                      <div className="reminder-details">
                        <span className="reminder-time">{reminder.time}</span>
                        <span className="reminder-frequency">{formatFrequency(reminder)}</span>
                        <span className="reminder-next">{formatNextSend(reminder.next_send)}</span>
                      </div>
                      <div className="reminder-method">
                        {(() => {
                          const methods = Array.isArray(reminder.notification_methods) 
                            ? reminder.notification_methods 
                            : JSON.parse(reminder.notification_methods || '[]');
                          return (
                            <>
                              {methods.includes('push') && '📱'}
                              {methods.includes('email') && '📧'}
                              {methods.includes('both') && '📱📧'}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    
                    <div className="reminder-actions">
                      <button
                        className={`toggle-btn ${reminder.enabled ? 'active' : 'inactive'}`}
                        onClick={() => toggleReminder(reminder.id)}
                        title={reminder.enabled ? 'Desactivar' : 'Activar'}
                      >
                        {reminder.enabled ? '🔔' : '🔕'}
                      </button>
                      <button
                        className="edit-btn"
                        onClick={() => handleEdit(reminder)}
                        title="Editar"
                      >
                        ✏️
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(reminder.id)}
                        title="Eliminar"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Formulario de nuevo/editar recordatorio */}
            {showForm && (
              <div className="reminder-form-container">
                <div className="form-header">
                  <h5>{editingId ? 'Editar recordatorio' : 'Nuevo recordatorio'}</h5>
                  <button 
                    className="close-form-btn"
                    onClick={() => {
                      setShowForm(false);
                      setEditingId(null);
                    }}
                  >
                    ✕
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="reminder-form">
                  {/* Templates predefinidos */}
                  <div className="templates-section">
                    <label>Plantillas rápidas:</label>
                    <div className="templates-grid">
                      {REMINDER_TEMPLATES.map(template => (
                        <button
                          key={template.id}
                          type="button"
                          className="template-btn"
                          style={{ borderLeft: `4px solid ${template.color}` }}
                          onClick={() => handleTemplateSelect(template.id)}
                        >
                          <span className="template-icon">{template.icon}</span>
                          <span className="template-title">{template.title}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Campos del formulario */}
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="reminder-title">Título:</label>
                      <input
                        id="reminder-title"
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        required
                        maxLength={50}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="reminder-message">Mensaje:</label>
                      <textarea
                        id="reminder-message"
                        value={formData.message}
                        onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                        required
                        maxLength={150}
                        rows={2}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="reminder-time">Hora:</label>
                      <input
                        id="reminder-time"
                        type="time"
                        value={formData.time}
                        onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="reminder-frequency">Frecuencia:</label>
                      <select
                        id="reminder-frequency"
                        value={formData.frequency}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          frequency: e.target.value as any,
                          days_of_week: e.target.value === 'weekly' ? [1, 2, 3, 4, 5] : []
                        }))}
                        required
                      >
                        <option value="daily">Diario</option>
                        <option value="weekly">Semanal</option>
                        <option value="monthly">Mensual</option>
                      </select>
                    </div>
                  </div>

                  {/* Días de la semana para frecuencia semanal */}
                  {formData.frequency === 'weekly' && (
                    <div className="form-row">
                      <div className="form-group full-width">
                        <label>Días de la semana:</label>
                        <div className="days-selector">
                          {DAYS_OF_WEEK.map(day => (
                            <button
                              key={day.value}
                              type="button"
                              className={`day-btn ${formData.days_of_week?.includes(day.value) ? 'selected' : ''}`}
                              onClick={() => handleDayToggle(day.value)}
                              title={day.name}
                            >
                              {day.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="notification-method">Notificar por:</label>
                      <select
                        id="notification-method"
                        value={formData.notification_method}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          notification_method: e.target.value as any 
                        }))}
                        required
                      >
                        <option value="push">📱 Push (App)</option>
                        <option value="email">📧 Email</option>
                        <option value="both">📱📧 Ambos</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="save-btn">
                      {editingId ? 'Actualizar' : 'Crear'} recordatorio
                    </button>
                    <button 
                      type="button" 
                      className="cancel-btn"
                      onClick={() => {
                        setShowForm(false);
                        setEditingId(null);
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            )}

            {reminders.length === 0 && !showForm && (
              <div className="no-reminders">
                <p>No tienes recordatorios configurados</p>
                <button 
                  className="create-first-btn"
                  onClick={() => setShowForm(true)}
                >
                  Crear tu primer recordatorio
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RemindersManager;